import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useBackend } from '../backend/BackendContext';
import type { Person, Session } from './session';

interface AuthContextValue {
  person: Person | undefined;
  /** True while a saved session is being restored when the app opens (FR-028). */
  restoring: boolean;
  session: Session;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { session } = useBackend();
  const [person, setPerson] = useState<Person | undefined>(session.getCurrentPerson());
  const [restoring, setRestoring] = useState(true);

  useEffect(() => session.onChange(setPerson), [session]);

  useEffect(() => {
    let cancelled = false;
    session
      .restore()
      .catch(() => undefined)
      .then((restored) => {
        if (cancelled) return;
        if (restored) setPerson(restored);
        setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  const value: AuthContextValue = {
    person,
    restoring,
    session,
    signOut: () => session.signOut(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth() must be used within an <AuthProvider>');
  }
  return value;
}
